<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeletelFlowsAPI from 'dashboard/api/teletel/flows';
import ConditionRows from './ConditionRows.vue';
import { allNodeIds, walkTree } from './treeOps';
import VariablePicker from './VariablePicker.vue';

const props = defineProps({
  node: { type: Object, required: true },
  tree: { type: Object, required: true },
});
const emit = defineEmits(['deleteNode']);
const { t } = useI18n();
const store = useStore();
const attachments = ref([]);
const attachmentsLoading = ref(false);
const showAdvanced = ref(false);

// Mutations go through the (stable) data object of the selected node; the
// server-side TreeValidator remains the authority on save/publish.
const data = computed(() => props.node.data);
const type = computed(() => props.node.customType);
const stepLabels = computed(() => ({
  SendMessage: t('TELETEL_FLOWS.BUILDER.STEP.SendMessage'),
  QuestionStep: t('TELETEL_FLOWS.BUILDER.STEP.QuestionStep'),
  BranchStep: t('TELETEL_FLOWS.BUILDER.STEP.BranchStep'),
  Jump: t('TELETEL_FLOWS.BUILDER.STEP.Jump'),
  AssignTo: t('TELETEL_FLOWS.BUILDER.STEP.AssignTo'),
  WaitStep: t('TELETEL_FLOWS.BUILDER.STEP.WaitStep'),
  CloseConversation: t('TELETEL_FLOWS.BUILDER.STEP.CloseConversation'),
  CsatStep: t('TELETEL_FLOWS.BUILDER.STEP.CsatStep'),
  SendAttachment: t('TELETEL_FLOWS.BUILDER.STEP.SendAttachment'),
  DateTime: t('TELETEL_FLOWS.BUILDER.STEP.DateTime'),
  UpdateContactField: t('TELETEL_FLOWS.BUILDER.STEP.UpdateContactField'),
  UpdateContactTags: t('TELETEL_FLOWS.BUILDER.STEP.UpdateContactTags'),
  UpdateLifecycleStage: t('TELETEL_FLOWS.BUILDER.STEP.UpdateLifecycleStage'),
  AddComment: t('TELETEL_FLOWS.BUILDER.STEP.AddComment'),
  OpenConversation: t('TELETEL_FLOWS.BUILDER.STEP.OpenConversation'),
  TriggerByAnotherFlow: t('TELETEL_FLOWS.BUILDER.STEP.TriggerByAnotherFlow'),
  HTTPRequest: t('TELETEL_FLOWS.BUILDER.STEP.HTTPRequest'),
  WhatsAppList: t('TELETEL_FLOWS.BUILDER.STEP.WhatsAppList'),
  AIStep: t('TELETEL_FLOWS.BUILDER.STEP.AIStep'),
  SetVariable: t('TELETEL_FLOWS.BUILDER.STEP.SetVariable'),
}));
const settingsTitle = computed(() => {
  const typeLabel = stepLabels.value[type.value] || type.value;
  const nodeName = props.node.name?.trim();

  if (!nodeName || nodeName === type.value || nodeName === typeLabel) {
    return typeLabel;
  }

  return `${nodeName} — ${typeLabel}`;
});

const agents = computed(() => store.getters['agents/getAgents']);
// Attributes this account requires before a conversation may be resolved;
// the close step supplies them so the close does not fail (the account rule
// itself is never bypassed).
const requiredAttributes = computed(
  () => store.getters['teletelFlows/getMeta']?.requiredAttributes || []
);
watch(
  () => props.node,
  node => {
    if (node.customType === 'CloseConversation' && !node.data.attributes) {
      node.data.attributes = {};
    }
  },
  { immediate: true }
);
const closeAttributes = computed(() => data.value.attributes || {});
const teams = computed(() => store.getters['teams/getTeams']);
const lifecycleStages = computed(
  () => store.getters['customerLifecycle/getActiveStages'] || []
);
const lifecycleEnabled = computed(
  () => store.getters['customerLifecycle/isReady'] || false
);
const jumpTargets = computed(() =>
  allNodeIds(props.tree).filter(target => target.id !== props.node.id)
);
const savedVariables = computed(() => {
  const variables = new Set();
  walkTree(props.tree, currentNode => {
    if (currentNode.customType === 'QuestionStep') {
      const variable = currentNode.data?.saveResponse?.variable?.trim();
      if (variable) variables.add(variable);
    }
    if (currentNode.customType === 'AIStep') {
      const variable = currentNode.data?.outputVariable?.trim() || 'ai_result';
      if (variable) variables.add(variable);
    }
    if (currentNode.customType === 'SetVariable' && currentNode.data?.key) {
      variables.add(currentNode.data.key);
    }
    Object.keys(currentNode.data?.responseMapping || {}).forEach(variable =>
      variables.add(variable)
    );
  });
  return [...variables];
});
const hasAdvancedOptions = computed(() =>
  ['SendMessage', 'QuestionStep', 'AssignTo'].includes(type.value)
);
// WhatsApp buttons: the tapped title comes back as the answer, so a label
// longer than the 20-character cap would never match its own option.
const BUTTON_LABEL_MAX = 20;
const longButtonLabel = computed(
  () =>
    type.value === 'QuestionStep' &&
    data.value.question?.presentation === 'buttons' &&
    (data.value.question?.options || []).some(
      option => option.trim().length > BUTTON_LABEL_MAX
    )
);
const warning = computed(() => {
  const value = data.value;
  if (
    type.value === 'SendMessage' &&
    !value.payload?.[0]?.message?.text?.trim()
  )
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.MESSAGE_REQUIRED');
  if (type.value === 'QuestionStep' && !value.question?.text?.trim())
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.QUESTION_REQUIRED');
  if (type.value === 'QuestionStep' && !value.saveResponse?.variable?.trim())
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.VARIABLE_REQUIRED');
  if (
    type.value === 'AssignTo' &&
    value.assignTo === 'specific_user' &&
    !value.userId
  )
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.AGENT_REQUIRED');
  if (
    type.value === 'AssignTo' &&
    value.assignTo === 'specific_team' &&
    !value.teamId
  )
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.TEAM_REQUIRED');
  if (longButtonLabel.value)
    return t('TELETEL_FLOWS.BUILDER.WARNINGS.BUTTON_TOO_LONG');
  return '';
});

// Approved WhatsApp templates of the account's WhatsApp inboxes, for the
// closed-window fallback of a SendMessage step.
const whatsappTemplates = computed(() =>
  (store.getters['inboxes/getInboxes'] || [])
    .filter(inbox => inbox.channel_type === 'Channel::Whatsapp')
    .flatMap(inbox =>
      (inbox.message_templates || [])
        .filter(tpl => (tpl.status || '').toLowerCase() === 'approved')
        .map(tpl => ({
          key: `${tpl.name}|${tpl.language}`,
          name: tpl.name,
          language: tpl.language,
          inbox: inbox.name,
          body:
            (tpl.components || []).find(
              c => (c.type || '').toUpperCase() === 'BODY'
            )?.text || '',
        }))
    )
);
const templateKey = computed({
  get: () =>
    data.value.whatsappTemplate?.name
      ? `${data.value.whatsappTemplate.name}|${data.value.whatsappTemplate.language}`
      : '',
  set: key => {
    const tpl = whatsappTemplates.value.find(item => item.key === key);
    data.value.whatsappTemplate = tpl
      ? { name: tpl.name, language: tpl.language, params: {} }
      : null;
  },
});
const templateParams = computed(() => {
  const tpl = whatsappTemplates.value.find(
    item => item.key === templateKey.value
  );
  if (!tpl) return [];
  const keys = [...tpl.body.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(m => m[1]);
  return [...new Set(keys)];
});
const templateBody = computed(
  () =>
    whatsappTemplates.value.find(item => item.key === templateKey.value)
      ?.body || ''
);

const optionsText = computed({
  get: () => (data.value.question?.options || []).join('\n'),
  set: value => {
    data.value.question.options = value
      .split('\n')
      .map(option => option.trim())
      .filter(Boolean);
  },
});

const questionValidation = computed({
  get: () => data.value.question?.validation?.type || 'none',
  set: value => {
    data.value.question.validation = {
      ...(data.value.question.validation || {}),
      type: value,
    };
  },
});

// Labels are stored as an array; the editor exposes them as one comma
// separated line, same convention as the question options above.
const labelList = computed({
  get: () => (data.value.labels || []).join(', '),
  set: value => {
    data.value.labels = value
      .split(',')
      .map(label => label.trim())
      .filter(Boolean);
  },
});

const whatsAppItemsText = computed({
  get: () =>
    (data.value.items || [])
      .map(item => `${item.title || ''}|${item.value || item.title || ''}`)
      .join('\n'),
  set: value => {
    data.value.items = value
      .split('\n')
      .map(line => line.split('|').map(part => part.trim()))
      .filter(([title]) => title)
      .slice(0, 10)
      .map(([title, itemValue]) => ({
        title,
        value: itemValue || title,
      }));
  },
});

const httpHeadersText = computed({
  get: () =>
    Object.entries(data.value.headers || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n'),
  set: value => {
    data.value.headers = Object.fromEntries(
      value
        .split('\n')
        .map(line => line.split(':'))
        .map(([key, ...parts]) => [key?.trim(), parts.join(':').trim()])
        .filter(([key]) => key)
    );
  },
});

const httpMappingText = computed({
  get: () =>
    Object.entries(data.value.responseMapping || {})
      .map(([variable, path]) => `${variable}|${path}`)
      .join('\n'),
  set: value => {
    data.value.responseMapping = Object.fromEntries(
      value
        .split('\n')
        .map(line => line.split('|').map(part => part.trim()))
        .filter(([variable, path]) => variable && path)
    );
  },
});

watch(
  type,
  async value => {
    if (value !== 'SendAttachment' || attachments.value.length) return;

    attachmentsLoading.value = true;
    try {
      const response = await TeletelFlowsAPI.getAttachments();
      attachments.value = response.data.payload || [];
    } finally {
      attachmentsLoading.value = false;
    }
  },
  { immediate: true }
);

watch(
  type,
  async value => {
    if (value !== 'UpdateLifecycleStage' || lifecycleStages.value.length)
      return;
    try {
      const settings = await store.dispatch('customerLifecycle/getSettings');
      if (settings?.available && settings?.enabled) {
        await store.dispatch('customerLifecycle/getStages');
      }
    } catch {
      // The server validator remains authoritative and will reject publishing
      // an unavailable lifecycle action.
    }
  },
  { immediate: true }
);

const lifecycleClear = computed({
  get: () => Boolean(data.value.clear),
  set: value => {
    data.value.clear = value;
    if (value) data.value.stageId = null;
  },
});

watch(
  () => props.node.id,
  () => {
    showAdvanced.value = false;
  }
);

// A QuestionStep tree is valid without a `timeout` object (it is optional),
// so the editor must never assume it exists. These lazily create it only when
// the user actually edits, keeping getters side-effect free.
const ensureTimeout = () => {
  if (!data.value.timeout) {
    data.value.timeout = { hasTimeout: false, value: 10, unit: 'minutes' };
  }
  return data.value.timeout;
};
const timeoutEnabled = computed({
  get: () => Boolean(data.value.timeout?.hasTimeout),
  set: value => {
    ensureTimeout().hasTimeout = value;
  },
});
const timeoutValue = computed({
  get: () => data.value.timeout?.value ?? 10,
  set: value => {
    ensureTimeout().value = value;
  },
});
const timeoutUnit = computed({
  get: () => data.value.timeout?.unit ?? 'minutes',
  set: value => {
    ensureTimeout().unit = value;
  },
});

const addMessage = () =>
  data.value.payload.push({ message: { type: 'text', text: '' } });

const deletable = computed(
  () => !['Trigger', 'AnswerStep', 'ConditionStep'].includes(type.value)
);
</script>

<template>
  <div class="flex flex-col gap-3 text-sm">
    <p class="font-medium text-n-slate-12">
      {{ settingsTitle }}
    </p>
    <p
      v-if="warning"
      class="rounded-lg bg-n-amber-3 px-2 py-1.5 text-xs text-n-amber-11"
    >
      {{ warning }}
    </p>

    <!-- SendMessage -->
    <template v-if="type === 'SendMessage'">
      <div
        v-for="(item, index) in data.payload"
        :key="index"
        class="flex flex-col gap-1"
      >
        <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.MESSAGE_TEXT') }}</label>
        <textarea v-model="item.message.text" rows="3" class="mb-0" />
        <VariablePicker
          v-model="item.message.text"
          :saved-variables="savedVariables"
        />
      </div>
      <button type="button" class="text-n-brand text-start" @click="addMessage">
        {{ `+ ${t('TELETEL_FLOWS.BUILDER.FORM.ADD_MESSAGE')}` }}
      </button>
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.VARIABLES_HINT') }}
      </p>
      <template v-if="showAdvanced">
        <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.WA_TEMPLATE') }}</label>
        <select v-model="templateKey" class="mb-0">
          <option value="">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.WA_TEMPLATE_NONE') }}
          </option>
          <option
            v-for="tpl in whatsappTemplates"
            :key="tpl.key"
            :value="tpl.key"
          >
            {{ tpl.inbox }} — {{ tpl.name }} ({{ tpl.language }})
          </option>
        </select>
        <p class="text-xs text-n-slate-10">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.WA_TEMPLATE_HINT') }}
        </p>
        <p v-if="templateBody" class="text-xs text-n-slate-11">
          {{ templateBody }}
        </p>
        <div
          v-for="param in templateParams"
          :key="param"
          class="flex flex-col gap-1"
        >
          <label>
            {{ t('TELETEL_FLOWS.BUILDER.FORM.WA_TEMPLATE_PARAM') }} {{ param }}
          </label>
          <input
            v-model="data.whatsappTemplate.params[param]"
            type="text"
            class="mb-0"
          />
        </div>
      </template>
    </template>

    <!-- QuestionStep -->
    <template v-else-if="type === 'QuestionStep'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.QUESTION_TEXT') }}</label>
      <textarea v-model="data.question.text" rows="3" class="mb-0" />
      <VariablePicker
        v-model="data.question.text"
        :saved-variables="savedVariables"
      />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.QUESTION_TYPE') }}</label>
      <select v-model="data.question.type" class="mb-0">
        <option value="text">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TYPE_TEXT') }}
        </option>
        <option value="multiple">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TYPE_MULTIPLE') }}
        </option>
      </select>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ANSWER_VALIDATION') }}</label>
      <select v-model="questionValidation" class="mb-0">
        <option value="none">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.VALIDATION_NONE') }}
        </option>
        <option value="email">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.VALIDATION_EMAIL') }}
        </option>
        <option value="phone">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.VALIDATION_PHONE') }}
        </option>
        <option value="number">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.VALIDATION_NUMBER') }}
        </option>
      </select>
      <template v-if="data.question.type === 'multiple'">
        <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.OPTIONS') }}</label>
        <textarea v-model="optionsText" rows="4" class="mb-0" />
        <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.PRESENTATION') }}</label>
        <select v-model="data.question.presentation" class="mb-0">
          <option value="text">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.PRESENTATION_TEXT') }}
          </option>
          <option value="buttons">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.PRESENTATION_BUTTONS') }}
          </option>
        </select>
        <p class="text-xs text-n-slate-10">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.PRESENTATION_HINT') }}
        </p>
      </template>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.SAVE_VARIABLE') }}</label>
      <input v-model="data.saveResponse.variable" class="mb-0" />
      <label v-if="showAdvanced" class="flex items-center gap-1">
        <input v-model="timeoutEnabled" type="checkbox" class="mb-0" />
        {{ t('TELETEL_FLOWS.BUILDER.FORM.TIMEOUT_ENABLED') }}
      </label>
      <div v-if="showAdvanced && timeoutEnabled" class="flex gap-2">
        <input
          v-model.number="timeoutValue"
          type="number"
          min="1"
          class="w-24 mb-0"
        />
        <select v-model="timeoutUnit" class="mb-0">
          <option value="minutes">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.UNIT_MINUTES') }}
          </option>
          <option value="hours">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.UNIT_HOURS') }}
          </option>
        </select>
      </div>
    </template>

    <!-- ConditionStep (branch leg) -->
    <template v-else-if="type === 'ConditionStep'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.CONDITIONS') }}</label>
      <ConditionRows
        :conditions="data.conditions || []"
        :can-add="node.name !== 'Else'"
        :lifecycle-enabled="lifecycleEnabled"
        :lifecycle-stages="lifecycleStages"
      />
    </template>

    <!-- Jump -->
    <template v-else-if="type === 'Jump'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.JUMP_TARGET') }}</label>
      <select v-model="data.stepId" class="mb-0">
        <option
          v-for="target in jumpTargets"
          :key="target.id"
          :value="target.id"
        >
          {{ target.label }}
        </option>
      </select>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.MAX_JUMPS') }}</label>
      <input
        v-model.number="data.maxJumps"
        type="number"
        min="1"
        class="mb-0"
      />
    </template>

    <!-- AssignTo -->
    <template v-else-if="type === 'AssignTo'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_MODE') }}</label>
      <select v-model="data.assignTo" class="mb-0">
        <option value="workspace">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_WORKSPACE') }}
        </option>
        <option value="specific_user">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_USER') }}
        </option>
        <option value="specific_team">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_TEAM') }}
        </option>
        <option value="unassign">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_UNASSIGN') }}
        </option>
      </select>
      <template v-if="data.assignTo === 'specific_user'">
        <select v-model="data.userId" class="mb-0">
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </template>
      <template v-if="data.assignTo === 'specific_team'">
        <select v-model="data.teamId" class="mb-0">
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </template>
      <template v-if="data.assignTo === 'workspace'">
        <template v-if="showAdvanced">
          <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ASSIGN_LOGIC') }}</label>
          <select v-model="data.assignmentLogic" class="mb-0">
            <option value="RoundRobin">
              {{ t('TELETEL_FLOWS.BUILDER.FORM.ROUND_ROBIN') }}
            </option>
            <option value="LeastContacts">
              {{ t('TELETEL_FLOWS.BUILDER.FORM.LEAST_CONTACTS') }}
            </option>
          </select>
          <label class="flex items-center gap-1">
            <input
              v-model="data.onlyOnlineUsers"
              type="checkbox"
              class="mb-0"
            />
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ONLY_ONLINE') }}
          </label>
        </template>
      </template>
    </template>

    <!-- WaitStep -->
    <template v-else-if="type === 'WaitStep'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VALUE') }}</label>
      <div class="flex gap-2">
        <input
          v-model.number="data.value"
          type="number"
          min="1"
          class="w-24 mb-0"
        />
        <select v-model="data.unit" class="mb-0">
          <option value="minutes">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.UNIT_MINUTES') }}
          </option>
          <option value="hours">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.UNIT_HOURS') }}
          </option>
        </select>
      </div>
    </template>

    <template v-else-if="type === 'DateTime'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VALUE') }}</label>
      <input v-model="data.at" type="datetime-local" class="mb-0" />
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.VARIABLES_HINT') }}
      </p>
    </template>

    <template v-else-if="type === 'UpdateContactField'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ATTRIBUTE') }}</label>
      <input v-model="data.key" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VALUE') }}</label>
      <input v-model="data.value" class="mb-0" />
      <VariablePicker v-model="data.value" :saved-variables="savedVariables" />
    </template>

    <template v-else-if="type === 'SetVariable'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VARIABLE_NAME') }}</label>
      <input v-model="data.key" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VALUE') }}</label>
      <input v-model="data.value" class="mb-0" />
      <VariablePicker v-model="data.value" :saved-variables="savedVariables" />
    </template>

    <template v-else-if="type === 'AddComment'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.MESSAGE_TEXT') }}</label>
      <textarea v-model="data.text" rows="3" class="mb-0" />
      <VariablePicker v-model="data.text" :saved-variables="savedVariables" />
    </template>

    <template v-else-if="type === 'UpdateContactTags'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_TARGET') }}</label>
      <select v-model="data.target" class="mb-0">
        <option value="contact">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_TARGET_CONTACT') }}
        </option>
        <option value="conversation">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_TARGET_CONVERSATION') }}
        </option>
        <option value="both">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_TARGET_BOTH') }}
        </option>
      </select>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_TARGET_HINT') }}
      </p>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_OPERATION') }}</label>
      <select v-model="data.operation" class="mb-0">
        <option value="add">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_ADD') }}
        </option>
        <option value="remove">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_REMOVE') }}
        </option>
      </select>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.TAGS_LIST') }}</label>
      <input
        v-model="labelList"
        class="mb-0"
        :placeholder="t('TELETEL_FLOWS.BUILDER.FORM.TAGS_LIST_HINT')"
      />
    </template>

    <template v-else-if="type === 'UpdateLifecycleStage'">
      <label class="flex items-center gap-2">
        <input v-model="lifecycleClear" type="checkbox" class="mb-0" />
        {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_CLEAR_STAGE') }}
      </label>
      <template v-if="!lifecycleClear">
        <label>{{
          t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_TARGET_STAGE')
        }}</label>
        <select v-model="data.stageId" class="mb-0 text-start">
          <option :value="null">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_PICK_STAGE') }}
          </option>
          <option
            v-for="stage in lifecycleStages"
            :key="stage.id"
            :value="stage.id"
          >
            {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
          </option>
        </select>
      </template>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_REASON') }}</label>
      <input
        v-model="data.reason"
        maxlength="1000"
        class="mb-0"
        :placeholder="t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_REASON_HINT')"
      />
    </template>

    <template v-else-if="type === 'TriggerByAnotherFlow'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.VALUE') }}</label>
      <input v-model.number="data.flowId" type="number" min="1" class="mb-0" />
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.HANDOFF_HINT') }}
      </p>
    </template>

    <template v-else-if="type === 'WhatsAppList'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.LIST_TITLE') }}</label>
      <input v-model="data.title" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.LIST_BODY') }}</label>
      <textarea v-model="data.body" rows="3" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.LIST_ITEMS') }}</label>
      <textarea v-model="whatsAppItemsText" rows="6" class="mb-0" />
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.LIST_ITEMS_HINT') }}
      </p>
    </template>

    <template v-else-if="type === 'SendAttachment'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ATTACHMENT') }}</label>
      <select
        v-model="data.attachmentId"
        class="mb-0"
        :disabled="attachmentsLoading"
      >
        <option :value="null">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTACHMENT_SELECT') }}
        </option>
        <option
          v-for="attachment in attachments"
          :key="attachment.id"
          :value="attachment.id"
        >
          {{
            `${attachment.filename} (${Math.ceil(attachment.byte_size / 1024)} KB)`
          }}
        </option>
      </select>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.ATTACHMENT_CAPTION') }}</label>
      <textarea v-model="data.caption" rows="2" class="mb-0" />
      <p
        v-if="!attachmentsLoading && !attachments.length"
        class="text-xs text-n-slate-10"
      >
        {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTACHMENT_EMPTY') }}
      </p>
    </template>

    <template v-else-if="type === 'HTTPRequest'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_METHOD') }}</label>
      <select v-model="data.method" class="mb-0">
        <option value="GET">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_GET') }}
        </option>
        <option value="POST">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_POST') }}
        </option>
      </select>
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_URL') }}</label>
      <input v-model="data.url" type="url" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_HEADERS') }}</label>
      <textarea v-model="httpHeadersText" rows="3" class="mb-0" />
      <label v-if="data.method === 'POST'">{{
        t('TELETEL_FLOWS.BUILDER.FORM.HTTP_BODY')
      }}</label>
      <textarea
        v-if="data.method === 'POST'"
        v-model="data.body"
        rows="4"
        class="mb-0"
      />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_MAPPING') }}</label>
      <textarea v-model="httpMappingText" rows="4" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_ERROR_VARIABLE') }}</label>
      <input v-model="data.errorVariable" class="mb-0" />
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.HTTP_HINT') }}
      </p>
    </template>

    <template v-else-if="type === 'AIStep'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.AI_PROMPT') }}</label>
      <textarea v-model="data.prompt" rows="4" class="mb-0" />
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.AI_OUTPUT_VARIABLE') }}</label>
      <input
        v-model="data.outputVariable"
        type="text"
        :placeholder="t('TELETEL_FLOWS.BUILDER.FORM.AI_OUTPUT_EXAMPLE')"
      />
      <p class="text-xs text-n-slate-10">
        {{ t('TELETEL_FLOWS.BUILDER.FORM.VARIABLES_HINT') }}
      </p>
    </template>

    <!-- CloseConversation -->
    <template v-else-if="type === 'CloseConversation'">
      <label>{{ t('TELETEL_FLOWS.BUILDER.FORM.CLOSE_SUMMARY') }}</label>
      <textarea v-model="data.summary" rows="2" class="mb-0" />
      <template v-if="requiredAttributes.length">
        <p class="text-xs text-n-slate-11">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.CLOSE_ATTRIBUTES_HINT') }}
        </p>
        <div
          v-for="attribute in requiredAttributes"
          :key="attribute.key"
          class="flex flex-col gap-1"
        >
          <label>{{ attribute.display_name }}</label>
          <select
            v-if="attribute.type === 'list'"
            v-model="closeAttributes[attribute.key]"
            class="mb-0"
          >
            <option value="">—</option>
            <option
              v-for="value in attribute.values"
              :key="value"
              :value="value"
            >
              {{ value }}
            </option>
          </select>
          <input
            v-else
            v-model="closeAttributes[attribute.key]"
            type="text"
            class="mb-0"
          />
        </div>
      </template>
    </template>

    <button
      v-if="hasAdvancedOptions"
      type="button"
      class="self-start text-xs font-medium text-n-brand hover:underline"
      :aria-expanded="showAdvanced"
      @click="showAdvanced = !showAdvanced"
    >
      {{
        showAdvanced
          ? t('TELETEL_FLOWS.BUILDER.HIDE_ADVANCED')
          : t('TELETEL_FLOWS.BUILDER.SHOW_ADVANCED')
      }}
    </button>

    <NextButton
      v-if="deletable"
      ruby
      xs
      class="mt-2 self-start"
      @click="emit('deleteNode', node.id)"
    >
      {{ t('TELETEL_FLOWS.BUILDER.DELETE_NODE') }}
    </NextButton>
  </div>
</template>
